import { motion } from 'framer-motion'
import {
  Mail,
  Users,
  TrendingUp,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle2,
  XCircle,
  Clock,
  UserCheck,
  UserX,
  UserPlus,
  Inbox,
  ExternalLink,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useDashboardStats, usePerformanceMetrics, useTrackingSummary } from '@hooks/useDashboard'
import { EmailChart } from '@components/dashboard/EmailChart'
import { RecentActivity } from '@components/dashboard/RecentActivity'
import { formatNumber } from '@lib/utils'
import { cn } from '@lib/utils'

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut' as const,
    },
  },
}

// Stat Card Component - Professional Glassmorphism
interface StatCardProps {
  title: string
  value: string | number
  subtitle: string
  icon: React.ElementType
  trend?: number
  iconColor: 'cyan' | 'indigo' | 'emerald' | 'amber'
  delay?: number
  isLoading?: boolean
}

const iconColorClasses = {
  cyan: 'text-cyan-500',
  indigo: 'text-indigo-500',
  emerald: 'text-emerald-500',
  amber: 'text-amber-500',
}

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  iconColor,
  delay = 0,
  isLoading = false,
}: StatCardProps) => {
  const isPositive = trend && trend >= 0

  return (
    <motion.div
      variants={itemVariants}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      className="stat-card"
    >
      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between">
          {/* Professional icon container */}
          <div className="icon-container icon-container-md">
            <Icon className={cn('w-5 h-5', iconColorClasses[iconColor])} />
          </div>
          {trend !== undefined && (
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: delay + 0.3 }}
              className={cn(
                'flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium',
                isPositive
                  ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
                  : 'bg-rose-500/15 text-rose-400 border border-rose-500/20'
              )}
            >
              {isPositive ? (
                <ArrowUpRight size={12} />
              ) : (
                <ArrowDownRight size={12} />
              )}
              <span>{Math.abs(trend)}%</span>
            </motion.div>
          )}
        </div>

        {/* Value */}
        <div className="mt-4">
          {isLoading ? (
            <div className="h-9 w-24 skeleton rounded-lg" />
          ) : (
            <h3 className="text-3xl font-bold text-slate-100 tracking-tight">
              {value}
            </h3>
          )}
        </div>

        {/* Title & Subtitle */}
        <div className="mt-2">
          <p className="text-sm font-medium text-slate-300">{title}</p>
          <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
    </motion.div>
  )
}

// Metric Ring Component
interface MetricRingProps {
  value: number
  label: string
  color: string
  size?: number
}

const MetricRing = ({ value, label, color, size = 80 }: MetricRingProps) => {
  const strokeWidth = 6
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI
  const offset = circumference - (value / 100) * circumference

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 150, damping: 15 }}
      className="flex flex-col items-center gap-2"
    >
      <div className="relative" style={{ width: size, height: size }}>
        {/* Background ring */}
        <svg className="absolute inset-0 -rotate-90" style={{ width: size, height: size }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-slate-700/50"
          />
        </svg>

        {/* Progress ring */}
        <svg className="absolute inset-0 -rotate-90" style={{ width: size, height: size }}>
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
          />
        </svg>

        {/* Value */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold text-slate-100">{value}%</span>
        </div>
      </div>
      <span className="text-xs font-medium text-slate-500">{label}</span>
    </motion.div>
  )
}


export function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useDashboardStats()
  const { data: performance, isLoading: perfLoading } = usePerformanceMetrics()
  const { data: tracking, isLoading: trackingLoading } = useTrackingSummary()
  const navigate = useNavigate()

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8 relative z-10"
    >
      {/* Welcome Section */}
      <motion.div variants={itemVariants}>
        <h1 className="text-2xl font-bold text-slate-100">
          Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}
        </h1>
        <p className="text-slate-500 mt-1">
          Here's what's happening with your email campaigns today.
        </p>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        variants={containerVariants}
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
      >
        <StatCard
          title="Total Campaigns"
          value={statsLoading ? '...' : formatNumber(stats?.totalCampaigns || 0)}
          subtitle={`${stats?.activeCampaigns || 0} currently active`}
          icon={Mail}
          trend={12}
          iconColor="cyan"
          isLoading={statsLoading}
        />
        <StatCard
          title="Total Clients"
          value={statsLoading ? '...' : formatNumber(stats?.totalClients || 0)}
          subtitle="Subscribers in database"
          icon={Users}
          trend={8}
          iconColor="indigo"
          isLoading={statsLoading}
        />
        <StatCard
          title="Emails Sent Today"
          value={statsLoading ? '...' : formatNumber(stats?.emailsSentToday || 0)}
          subtitle={`${formatNumber(stats?.emailsSentThisWeek || 0)} this week`}
          icon={TrendingUp}
          trend={23}
          iconColor="emerald"
          isLoading={statsLoading}
        />
        <StatCard
          title="Delivery Rate"
          value={perfLoading ? '...' : `${(performance?.deliveryRate || 0).toFixed(1)}%`}
          subtitle={`${(performance?.openRate || 0).toFixed(1)}% open rate`}
          icon={Activity}
          trend={5}
          iconColor="amber"
          isLoading={perfLoading}
        />
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Chart Section */}
        <motion.div
          variants={itemVariants}
          className="lg:col-span-2 stat-card"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-slate-100">
                Email Performance
              </h2>
              <p className="text-sm text-slate-500">Last 7 days</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-cyan-500" />
                <span className="text-xs text-slate-500">Sent</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-xs text-slate-500">Delivered</span>
              </div>
            </div>
          </div>
          <EmailChart />
        </motion.div>

        {/* Performance Rings */}
        <motion.div
          variants={itemVariants}
          className="stat-card"
        >
          <h2 className="text-lg font-semibold text-slate-100 mb-6">
            Performance Metrics
          </h2>

          <div className="grid grid-cols-2 gap-6">
            <MetricRing
              value={Math.round(performance?.deliveryRate || 0)}
              label="Delivery"
              color="#22d3ee"
            />
            <MetricRing
              value={Math.round(performance?.openRate || 0)}
              label="Open Rate"
              color="#818cf8"
            />
            <MetricRing
              value={Math.round(performance?.replyRate || 0)}
              label="Reply Rate"
              color="#a78bfa"
            />
            <MetricRing
              value={Math.round(performance?.bounceRate || 0)}
              label="Bounce Rate"
              color="#f87171"
            />
          </div>

          {/* Quick Stats */}
          <div className="mt-6 pt-6 border-t border-slate-700/50 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-emerald-500" />
                <span className="text-sm text-slate-500">Successful</span>
              </div>
              <span className="text-sm font-semibold text-slate-300">
                {formatNumber(stats?.emailsSentThisWeek || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <XCircle size={16} className="text-rose-500" />
                <span className="text-sm text-slate-500">Failed</span>
              </div>
              <span className="text-sm font-semibold text-slate-300">
                {formatNumber(Math.round((stats?.emailsSentThisWeek || 0) * (performance?.bounceRate || 0) / 100))}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-amber-500" />
                <span className="text-sm text-slate-500">Pending</span>
              </div>
              <span className="text-sm font-semibold text-slate-300">
                {formatNumber(0)}
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Activity */}
        <motion.div
          variants={itemVariants}
          className="stat-card"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-slate-100">
              Recent Activity
            </h2>
            <button className="text-sm text-cyan-500 font-medium hover:text-cyan-400 transition-colors">
              View all
            </button>
          </div>
          <RecentActivity />
        </motion.div>

        {/* Tracking Summary */}
        <motion.div
          variants={itemVariants}
          className="stat-card"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-slate-100">
              Tracking Summary
            </h2>
            <button
              onClick={() => navigate('/tracking')}
              className="text-sm text-cyan-500 font-medium hover:text-cyan-400 transition-colors flex items-center gap-1"
            >
              View Tracking
              <ExternalLink size={12} />
            </button>
          </div>

          <div className="space-y-4">
            {/* Responders */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                  <UserCheck size={18} className="text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-200">Responders</p>
                  <p className="text-xs text-slate-500">Replied to campaigns</p>
                </div>
              </div>
              {trackingLoading ? (
                <div className="h-7 w-10 skeleton rounded" />
              ) : (
                <span className="text-xl font-bold text-emerald-400">{tracking?.responders ?? 0}</span>
              )}
            </div>

            {/* Non-responders */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-rose-500/5 border border-rose-500/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-rose-500/15 flex items-center justify-center">
                  <UserX size={18} className="text-rose-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-200">Non-Responders</p>
                  <p className="text-xs text-slate-500">Archived, no reply</p>
                </div>
              </div>
              {trackingLoading ? (
                <div className="h-7 w-10 skeleton rounded" />
              ) : (
                <span className="text-xl font-bold text-rose-400">{tracking?.nonResponders ?? 0}</span>
              )}
            </div>

            {/* New / Active */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-cyan-500/5 border border-cyan-500/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-cyan-500/15 flex items-center justify-center">
                  <UserPlus size={18} className="text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-200">New / Active</p>
                  <p className="text-xs text-slate-500">In active sequences</p>
                </div>
              </div>
              {trackingLoading ? (
                <div className="h-7 w-10 skeleton rounded" />
              ) : (
                <span className="text-xl font-bold text-cyan-400">{tracking?.newActive ?? 0}</span>
              )}
            </div>

            {/* Inbox Unhandled */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-amber-500/5 border border-amber-500/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
                  <Inbox size={18} className="text-amber-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-200">Inbox Unhandled</p>
                  <p className="text-xs text-slate-500">Needs attention</p>
                </div>
              </div>
              {trackingLoading ? (
                <div className="h-7 w-10 skeleton rounded" />
              ) : (
                <span className="text-xl font-bold text-amber-400">{tracking?.inboxUnhandled ?? 0}</span>
              )}
            </div>
          </div>

          {/* Quick links */}
          <div className="mt-4 pt-4 border-t border-slate-700/50 flex gap-3">
            <button
              onClick={() => navigate('/tracking')}
              className="flex-1 py-2 text-sm text-center text-cyan-500 hover:text-cyan-400 bg-cyan-500/5 hover:bg-cyan-500/10 rounded-lg border border-cyan-500/10 transition-colors"
            >
              View Tracking
            </button>
            <button
              onClick={() => navigate('/inbox')}
              className="flex-1 py-2 text-sm text-center text-amber-500 hover:text-amber-400 bg-amber-500/5 hover:bg-amber-500/10 rounded-lg border border-amber-500/10 transition-colors"
            >
              View Inbox
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}
